import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { authAPI } from '../services/api';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on app load (prefer localStorage)
    const tokenFromLS = localStorage.getItem('access_token');
    const userFromLS = localStorage.getItem('user');
    const tokenFromCookie = Cookies.get('access_token');
    const userFromCookie = Cookies.get('user');

    // One-time migration: if token exists in cookies but not in localStorage, copy it
    if (!tokenFromLS && tokenFromCookie) {
      localStorage.setItem('access_token', tokenFromCookie);
    }
    if (!userFromLS && userFromCookie) {
      localStorage.setItem('user', userFromCookie);
    }

    const savedToken = localStorage.getItem('access_token') || tokenFromCookie;
    const savedUserStr = localStorage.getItem('user') || userFromCookie;
    
    if (savedToken && savedUserStr) {
      try {
        setToken(savedToken);
        setUser(JSON.parse(savedUserStr));
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        logout();
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      
      // Save to localStorage (primary storage)
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Also save to cookies for backward compatibility with existing axios interceptors
      Cookies.set('access_token', access_token, { expires: 1 });
      Cookies.set('user', JSON.stringify(userData), { expires: 1 });
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: (error.response && error.response.data && error.response.data.error) || 'Login failed'
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    // Clear localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    // Clear cookies (if present)
    Cookies.remove('access_token');
    Cookies.remove('user');
  };

  const isAuthenticated = () => {
    return !!(token && user);
  };

  const value = {
    user,
    token,
    login,
    logout,
    isAuthenticated,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 